import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { api } from '../../utils/api';
import { getProductImage } from '../../utils/productUtils';

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';

const ProductThumbnail = ({ product, size = 72 }) => (
  <Box
    component="img"
    src={getProductImage(product.image)}
    alt={product.name}
    onError={(e) => {
      e.target.src = PLACEHOLDER_IMAGE;
    }}
    sx={{
      width: size,
      height: size,
      minWidth: size,
      objectFit: 'contain',
      bgcolor: '#f5f5f5',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider',
      p: 0.5
    }}
  />
);

const ProductDetails = ({ product, imageSize = 72 }) => {
  const description =
    product.description && product.description.trim() !== product.name?.trim()
      ? product.description.trim()
      : null;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
      <ProductThumbnail product={product} size={imageSize} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" noWrap>
          {product.code}
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
          {product.name}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const InventoryCountDialog = ({ open, onClose, onConfirmed }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [physicalQty, setPhysicalQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const resetForm = useCallback(() => {
    setSearch('');
    setResults([]);
    setSelected(null);
    setPhysicalQty('');
    setError(null);
    setSuccessMsg(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      return undefined;
    }

    if (!search.trim() || search.trim().length < 2) {
      setResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const res = await api.get('/products', {
          params: {
            search: search.trim(),
            type: 'Prodotto',
            limit: 15,
            page: 1
          }
        });
        const list = res.data?.products || (Array.isArray(res.data) ? res.data : []);
        setResults(list.filter((p) => !p.isLabor));
      } catch (err) {
        setError('Errore durante la ricerca');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [open, search]);

  const handleSelectProduct = (product) => {
    setSelected(product);
    setPhysicalQty(String(parseInt(product.stock, 10) || 0));
    setError(null);
    setSuccessMsg(null);
  };

  const handleSave = async () => {
    if (!selected) {
      setError('Seleziona un articolo dalla lista');
      return;
    }

    const qty = parseInt(physicalQty, 10);
    if (Number.isNaN(qty) || qty < 0) {
      setError('Inserisci una quantità valida (≥ 0)');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/products/${selected.id}/confirm-inventory`, {
        quantity: qty
      });
      const systemStock = parseInt(selected.stock, 10) || 0;
      const diff = qty - systemStock;
      let msg = `Inventario confermato: ${selected.code}`;
      if (diff !== 0) {
        msg += ` (giacenza aggiornata ${systemStock} → ${qty})`;
      }
      setSuccessMsg(msg);
      onConfirmed?.(res.data);
      setSelected(null);
      setPhysicalQty('');
      setSearch('');
      setResults([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>Inventario fisico</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 400 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cerca per codice o descrizione, inserisci la quantità realmente presente e salva.
          Se differisce dalla giacenza di sistema verrà creato un movimento di rettifica.
        </Typography>

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
            {successMsg}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Codice o descrizione"
          placeholder="Es. LUCCHETTO, 843576..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelected(null);
          }}
          autoFocus
          sx={{ mb: 2 }}
        />

        {searching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={36} />
          </Box>
        )}

        {!searching && search.trim().length >= 2 && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Nessun articolo trovato
          </Typography>
        )}

        {results.length > 0 && !selected && (
          <List
            sx={{
              maxHeight: 440,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            {results.map((product) => (
              <ListItemButton
                key={product.id}
                onClick={() => handleSelectProduct(product)}
                sx={{
                  py: 1.5,
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'flex-start' }}>
                  <ProductDetails product={product} imageSize={80} />
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Giacenza: {product.stock ?? 0}
                    </Typography>
                    {product.inventoryConfirmed && (
                      <Chip size="small" color="success" label="Confermato" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}

        {selected && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                alignItems: 'flex-start',
                mb: 2
              }}
            >
              <ProductThumbnail product={selected} size={160} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  Codice
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {selected.code}
                </Typography>
                <Typography variant="overline" color="text.secondary">
                  Descrizione
                </Typography>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  {selected.name}
                </Typography>
                {selected.description &&
                  selected.description.trim() !== selected.name?.trim() && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selected.description}
                    </Typography>
                  )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Chip
                    size="small"
                    label={`Giacenza sistema: ${selected.stock ?? 0}`}
                    variant="outlined"
                  />
                  {selected.inventoryConfirmed && (
                    <Chip size="small" color="success" label="Già confermato in precedenza" />
                  )}
                </Box>
              </Box>
            </Box>
            <TextField
              fullWidth
              type="number"
              label="Quantità fisica presente"
              value={physicalQty}
              onChange={(e) => setPhysicalQty(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              sx={{ maxWidth: 320 }}
            />
            <Button size="small" sx={{ mt: 1, display: 'block' }} onClick={() => setSelected(null)}>
              Cambia articolo
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Chiudi
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !selected}
        >
          {saving ? 'Salvataggio...' : 'Salva conteggio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryCountDialog;
